import { invoicedToDate, invoiceDeleteAllowed, normalizeInvoiceRole } from '../../modules/docs/utils/invoiceLedger';
import type {
  PaymentSchedule,
  PaymentScheduleStage,
  PaymentScheduleStageKind,
  PaymentScheduleStageStatus,
  PlannedStageDraft,
  PresentedScheduleStage,
  RetrospectiveMappingRow,
  ScheduleInvoice,
} from './paymentSchedule.types';

export const PAYMENT_SCHEDULE_ROUNDING_TOLERANCE = 0.02;

export const DEPOSIT_BALANCE_PATTERN = 'deposit/balance';

export const QUOTED_PAYMENT_STRUCTURES: Array<{ label: string; parts: number[] }> = [
  { label: '20/20/30/30', parts: [20, 20, 30, 30] },
  { label: '25/50/25', parts: [25, 50, 25] },
  { label: '30/30/40', parts: [30, 30, 40] },
  { label: '30/40/30', parts: [30, 40, 30] },
  { label: '40/30/30', parts: [40, 30, 30] },
  { label: '40/40/20', parts: [40, 40, 20] },
  { label: '50/25/25', parts: [50, 25, 25] },
  { label: '50/40/10', parts: [50, 40, 10] },
];

export function money(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round((n + Number.EPSILON) * 100) / 100 : 0;
}

export function amountsMatch(left: number, right: number): boolean {
  return Math.abs(money(left) - money(right)) <= PAYMENT_SCHEDULE_ROUNDING_TOLERANCE;
}

export function defaultStageLabels(count: number): string[] {
  if (count <= 1) return ['Final Balance'];
  if (count === 2) return ['Deposit', 'Final Balance'];
  if (count === 3) return ['Deposit', 'Progress', 'Final Balance'];
  if (count === 4) return ['Deposit', 'Commencement', 'Progress', 'Final Balance'];
  const mid = Array.from({ length: count - 2 }, (_, index) => `Interim ${index + 1}`);
  return ['Deposit', ...mid, 'Final Balance'];
}

export function stageKindForIndex(index: number, count: number): PaymentScheduleStageKind {
  if (count <= 1) return 'final';
  if (index === 0) return 'deposit';
  if (index === count - 1) return 'final';
  return 'interim';
}

export function issuedDepositLock(args: {
  invoices: ScheduleInvoice[];
  grossTotal?: number;
  depositPercentage?: number | null;
}): { amount: number; percent: number | null } | null {
  const deposit = scheduleableInvoices(args.invoices).find((invoice) => (
    normalizeInvoiceRole(invoice.role) === 'deposit'
  ));
  if (!deposit) return null;
  const amount = money(deposit.grossTotal || deposit.depositAmount);
  if (!(amount > 0)) return null;
  const quotedPct = Number(args.depositPercentage);
  const gross = money(args.grossTotal);
  const percent = quotedPct > 0 && quotedPct <= 1
    ? quotedPct
    : (gross > 0 ? amount / gross : null);
  return { amount, percent };
}

export function patternAgreesWithIssuedDeposit(args: {
  pattern: string;
  parts?: number[];
  grossTotal: number;
  issuedAmount: number;
  issuedPercent?: number | null;
}): boolean {
  const pattern = String(args.pattern || '').trim();
  if (pattern === DEPOSIT_BALANCE_PATTERN) return true;
  const parts = args.parts?.length
    ? args.parts
    : (QUOTED_PAYMENT_STRUCTURES.find((item) => item.label === pattern)?.parts
      || parsePatternParts(pattern));
  if (parts.length < 2) return false;
  const impliedAmount = money(args.grossTotal * (parts[0] / 100));
  if (!amountsMatch(impliedAmount, args.issuedAmount)) return false;
  if (args.issuedPercent != null && Number(args.issuedPercent) > 0) {
    return amountsMatch(parts[0] / 100, args.issuedPercent);
  }
  return true;
}

export function patternsCompatibleWithIssuedDeposit(args: {
  invoices: ScheduleInvoice[];
  grossTotal: number;
  depositPercentage?: number | null;
}): Array<{ label: string; parts: number[] }> {
  const lock = issuedDepositLock(args);
  if (!lock) return QUOTED_PAYMENT_STRUCTURES;
  return QUOTED_PAYMENT_STRUCTURES.filter((item) => patternAgreesWithIssuedDeposit({
    pattern: item.label,
    parts: item.parts,
    grossTotal: args.grossTotal,
    issuedAmount: lock.amount,
    issuedPercent: lock.percent,
  }));
}

export function schedulePatternChoices(args: {
  invoices: ScheduleInvoice[];
  grossTotal: number;
  depositPercentage?: number | null;
}): Array<{ label: string; parts: number[] }> {
  if (!issuedDepositLock(args)) return QUOTED_PAYMENT_STRUCTURES;
  // The issued deposit always stays as invoiced. Patterns that agree with it come first; the
  // rest stay available and only split what is left after the deposit in their own proportions.
  const compatible = patternsCompatibleWithIssuedDeposit(args);
  const others = QUOTED_PAYMENT_STRUCTURES.filter((item) => !compatible.includes(item));
  return compatible.length
    ? [...compatible, ...others]
    : [{ label: DEPOSIT_BALANCE_PATTERN, parts: [] }, ...others];
}

export function defaultCompatiblePattern(args: {
  quotedPattern?: string | null;
  choices: Array<{ label: string }>;
}): string {
  const quoted = String(args.quotedPattern || '').trim();
  if (quoted && args.choices.some((item) => item.label === quoted)) return quoted;
  return args.choices[0]?.label || DEPOSIT_BALANCE_PATTERN;
}

export function patternChoiceLabel(pattern: string): string {
  return pattern === DEPOSIT_BALANCE_PATTERN ? 'Deposit + Final Balance' : pattern;
}

export function parseQuotedPattern(args: {
  pattern?: string | null;
  grossTotal: number;
  depositAmount?: number | null;
}): PlannedStageDraft[] {
  const pattern = String(args.pattern || '').trim();
  if (pattern === DEPOSIT_BALANCE_PATTERN) {
    const gross = money(args.grossTotal);
    const depositAmount = money(args.depositAmount);
    return [
      {
        sortOrder: 0,
        label: 'Deposit',
        kind: 'deposit',
        plannedPercent: gross > 0 ? depositAmount / gross : null,
        plannedAmount: depositAmount,
      },
      {
        sortOrder: 1,
        label: 'Final Balance',
        kind: 'final',
        plannedPercent: null,
        plannedAmount: money(Math.max(gross - depositAmount, 0)),
      },
    ];
  }
  const preset = QUOTED_PAYMENT_STRUCTURES.find((item) => item.label === pattern);
  const parts = preset?.parts || parsePatternParts(pattern);
  if (parts.length < 2) return [];
  const labels = defaultStageLabels(parts.length);
  const gross = money(args.grossTotal);
  const quotedDeposit = money(args.depositAmount);
  const depositFromParts = money(gross * (parts[0] / 100));
  const depositAmount = quotedDeposit > 0 ? quotedDeposit : depositFromParts;
  const remaining = money(Math.max(gross - depositAmount, 0));
  const remainingParts = parts.slice(1);
  const remainingWeight = remainingParts.reduce((sum, part) => sum + part, 0) || 1;
  const drafts: PlannedStageDraft[] = [{
    sortOrder: 0,
    label: labels[0],
    kind: stageKindForIndex(0, parts.length),
    plannedPercent: parts[0] / 100,
    plannedAmount: depositAmount,
  }];
  let allocated = 0;
  remainingParts.forEach((part, index) => {
    const isLast = index === remainingParts.length - 1;
    const amount = isLast
      ? money(remaining - allocated)
      : money(remaining * (part / remainingWeight));
    if (!isLast) allocated = money(allocated + amount);
    drafts.push({
      sortOrder: index + 1,
      label: labels[index + 1],
      kind: stageKindForIndex(index + 1, parts.length),
      plannedPercent: part / 100,
      plannedAmount: amount,
    });
  });
  return drafts;
}

function parsePatternParts(pattern: string): number[] {
  return pattern
    .split('/')
    .map((part) => Number(String(part).trim()))
    .filter((part) => Number.isFinite(part) && part > 0);
}

export function liveScheduleInvoices(invoices: ScheduleInvoice[]): ScheduleInvoice[] {
  return invoices.filter((invoice) => String(invoice.status || '').toLowerCase() !== 'void');
}

export function scheduleableInvoices(invoices: ScheduleInvoice[]): ScheduleInvoice[] {
  return liveScheduleInvoices(invoices)
    .filter((invoice) => {
      const role = normalizeInvoiceRole(invoice.role);
      return role === 'deposit' || role === 'works';
    })
    .sort((a, b) => {
      const roleRank = (normalizeInvoiceRole(a.role) === 'deposit' ? 0 : 1)
        - (normalizeInvoiceRole(b.role) === 'deposit' ? 0 : 1);
      if (roleRank !== 0) return roleRank;
      return Date.parse(String(a.createdAt || '')) - Date.parse(String(b.createdAt || ''));
    });
}

export function issuedTotal(invoices: ScheduleInvoice[]): number {
  return invoicedToDate(liveScheduleInvoices(invoices));
}

export function remainingToInvoice(args: {
  grossTotal: number;
  invoices: ScheduleInvoice[];
}): number {
  return money(Math.max(money(args.grossTotal) - issuedTotal(args.invoices), 0));
}

export function futurePlannedTotal(stages: Array<Pick<PaymentScheduleStage, 'status' | 'plannedAmount'>>): number {
  return money(stages
    .filter((stage) => stage.status !== 'issued')
    .reduce((sum, stage) => sum + money(stage.plannedAmount), 0));
}

export function issuedStageTotal(stages: Array<Pick<PaymentScheduleStage, 'status' | 'issuedAmount'>>): number {
  return money(stages
    .filter((stage) => stage.status === 'issued')
    .reduce((sum, stage) => sum + money(stage.issuedAmount), 0));
}

export function scheduleNeedsReview(args: {
  grossTotal: number;
  agreedValueAtLastCalc?: number | null;
  stages: Array<Pick<PaymentScheduleStage, 'status' | 'plannedAmount' | 'issuedAmount'>>;
  invoices: ScheduleInvoice[];
}): boolean {
  const remaining = remainingToInvoice(args);
  const future = futurePlannedTotal(args.stages);
  if (!amountsMatch(future, remaining)) return true;
  if (args.agreedValueAtLastCalc != null && !amountsMatch(args.agreedValueAtLastCalc, args.grossTotal)) {
    return !amountsMatch(future, remaining);
  }
  return false;
}

export function applyIssuedInvoicesToDrafts(args: {
  drafts: PlannedStageDraft[];
  invoices: ScheduleInvoice[];
  grossTotal: number;
}): PlannedStageDraft[] {
  const issued = scheduleableInvoices(args.invoices);
  const drafts = args.drafts.map((draft, index) => {
    const invoice = issued[index];
    if (!invoice) {
      return { ...draft, invoiceId: null, issuedAmount: null, status: 'expected' as const };
    }
    const amount = money(invoice.grossTotal || invoice.depositAmount);
    return {
      ...draft,
      invoiceId: invoice.id,
      issuedAmount: amount,
      plannedAmount: amount,
      status: 'issued' as const,
    };
  });
  const redistributed = redistributeFuturePlanned({
    drafts,
    invoices: args.invoices,
    grossTotal: args.grossTotal,
  });
  return redistributed
    .filter((draft) => draft.status === 'issued' || money(draft.plannedAmount) > 0)
    .map((draft, index, list) => ({
      ...draft,
      sortOrder: index,
      kind: stageKindForIndex(index, list.length),
    }));
}

export function redistributeFuturePlanned(args: {
  drafts: PlannedStageDraft[];
  invoices: ScheduleInvoice[];
  grossTotal: number;
}): PlannedStageDraft[] {
  const remaining = remainingToInvoice(args);
  const futureIndexes = args.drafts
    .map((draft, index) => ({ draft, index }))
    .filter(({ draft }) => draft.status !== 'issued');
  if (futureIndexes.length === 0) return args.drafts;
  const weight = futureIndexes.reduce((sum, { draft }) => sum + (Number(draft.plannedPercent) > 0
    ? Number(draft.plannedPercent)
    : money(draft.plannedAmount)), 0) || futureIndexes.length;
  let allocated = 0;
  return args.drafts.map((draft, index) => {
    const futureIndex = futureIndexes.findIndex((item) => item.index === index);
    if (futureIndex < 0) return draft;
    const isLast = futureIndex === futureIndexes.length - 1;
    const share = Number(draft.plannedPercent) > 0 ? Number(draft.plannedPercent) : money(draft.plannedAmount);
    const amount = isLast
      ? money(remaining - allocated)
      : money(remaining * (share / weight));
    if (!isLast) allocated = money(allocated + amount);
    return { ...draft, plannedAmount: Math.max(amount, 0), status: draft.status || 'expected' };
  });
}

export function proposeRetrospectiveMapping(args: {
  pattern: string;
  grossTotal: number;
  invoices: ScheduleInvoice[];
  depositAmount?: number | null;
}): { drafts: PlannedStageDraft[]; rows: RetrospectiveMappingRow[] } {
  const base = parseQuotedPattern({
    pattern: args.pattern,
    grossTotal: args.grossTotal,
    depositAmount: args.depositAmount,
  });
  const drafts = applyIssuedInvoicesToDrafts({
    drafts: base,
    invoices: args.invoices,
    grossTotal: args.grossTotal,
  });
  const issued = scheduleableInvoices(args.invoices);
  const rows: RetrospectiveMappingRow[] = drafts.map((draft, index) => {
    const invoice = issued[index] || null;
    return {
      invoiceId: invoice?.id || null,
      invoiceRole: invoice ? normalizeInvoiceRole(invoice.role) : null,
      invoiceAmount: invoice ? money(invoice.grossTotal || invoice.depositAmount) : money(draft.plannedAmount),
      proposedLabel: draft.label,
      proposedKind: draft.kind,
      issued: !!invoice,
    };
  });
  return { drafts, rows };
}

export function resolveStageStatuses(args: {
  stages: PaymentScheduleStage[];
  finalSettled: boolean;
  firstAccepted?: boolean;
}): PaymentScheduleStage[] {
  let previousIssued = true;
  return args.stages.map((stage, index) => {
    if (stage.status === 'issued' || stage.invoiceId) {
      previousIssued = true;
      return { ...stage, status: 'issued' as const };
    }
    const waitingOnPrevious = index > 0 && !previousIssued;
    let status: PaymentScheduleStageStatus = 'expected';
    if (!waitingOnPrevious) {
      if (stage.kind === 'deposit') {
        status = args.firstAccepted === false ? 'expected' : 'ready';
      } else {
        status = args.finalSettled ? 'ready' : 'expected';
      }
    }
    previousIssued = false;
    return { ...stage, status };
  });
}

export function nextIssuableStage(stages: PaymentScheduleStage[]): PaymentScheduleStage | null {
  return stages.find((stage) => stage.status === 'ready' && !stage.invoiceId) || null;
}

export function nextIssueAmount(args: {
  stage: Pick<PaymentScheduleStage, 'kind' | 'plannedAmount'>;
  grossTotal: number;
  invoices: ScheduleInvoice[];
}): number {
  if (args.stage.kind === 'final') {
    return remainingToInvoice({ grossTotal: args.grossTotal, invoices: args.invoices });
  }
  return money(args.stage.plannedAmount);
}

export function lastIssuedStage(stages: PaymentScheduleStage[]): PaymentScheduleStage | null {
  const issued = stages.filter((stage) => stage.status === 'issued' || stage.invoiceId);
  return issued[issued.length - 1] || null;
}

export function invoiceDeleteAllowedWithSchedule(args: {
  invoice: { id?: string; invoiceRole?: string | null; role?: string | null; status?: string | null };
  invoices: Array<{ invoiceRole?: string | null; role?: string | null; status?: string | null }>;
  stages?: PaymentScheduleStage[] | null;
}): boolean {
  if (!invoiceDeleteAllowed(args.invoice, args.invoices)) return false;
  const role = normalizeInvoiceRole(args.invoice.invoiceRole ?? args.invoice.role);
  if (role === 'variation' || role === 'credit') return true;
  if (!args.stages?.length) return true;
  const last = lastIssuedStage(args.stages);
  if (!last?.invoiceId) return true;
  return String(args.invoice.id || '') === last.invoiceId;
}

export function isOrphanedIssuedStage(stage: PaymentScheduleStage): boolean {
  return stage.status === 'issued' && !stage.invoiceId;
}

/**
 * Deleting an invoice nulls `invoice_id` on its stage (FK on delete set null)
 * before the rollback can match it, which leaves an "issued" stage with no
 * invoice. Release those so the milestone is expected again, and re-base an
 * uninvoiced schedule onto the agreed total when its planned amounts drift.
 */
export function repriseScheduleStages(args: {
  stages: PaymentScheduleStage[];
  invoices: ScheduleInvoice[];
  grossTotal: number;
}): { stages: PlannedStageDraft[]; needsReview: boolean } | null {
  if (!(money(args.grossTotal) > 0) || args.stages.length === 0) return null;
  const hasOrphans = args.stages.some(isOrphanedIssuedStage);
  const nothingIssued = args.stages.every((stage) => stage.status !== 'issued' || isOrphanedIssuedStage(stage));
  const drifted = nothingIssued
    && Math.abs(futurePlannedTotal(args.stages) - remainingToInvoice(args)) > 0.01;
  if (!hasOrphans && !drifted) return null;
  const drafts: PlannedStageDraft[] = args.stages.map((stage) => {
    const release = isOrphanedIssuedStage(stage);
    return {
      sortOrder: stage.sortOrder,
      label: stage.label,
      kind: stage.kind,
      plannedPercent: stage.plannedPercent,
      plannedAmount: release
        ? stage.plannedAmount
        : (stage.status === 'issued' ? money(stage.issuedAmount) : stage.plannedAmount),
      invoiceId: release ? null : stage.invoiceId,
      issuedAmount: release ? null : stage.issuedAmount,
      status: release ? 'expected' : stage.status,
    };
  });
  const redistributed = redistributeFuturePlanned({
    drafts,
    invoices: args.invoices,
    grossTotal: args.grossTotal,
  });
  return {
    stages: redistributed,
    needsReview: scheduleNeedsReview({
      grossTotal: args.grossTotal,
      stages: redistributed.map((draft) => ({
        status: draft.status || 'expected',
        plannedAmount: draft.plannedAmount,
        issuedAmount: draft.issuedAmount ?? null,
      })),
      invoices: args.invoices,
    }),
  };
}

/**
 * A draft invoice bound to an issued stage can still have its amount edited (e.g. the deposit
 * corrected to what the customer actually paid). Carry the invoice's amount onto its stage and
 * re-split the uninvoiced remainder across future stages. Null when every stage already agrees.
 */
export function resyncIssuedStageAmounts(args: {
  stages: PaymentScheduleStage[];
  invoices: ScheduleInvoice[];
  grossTotal: number;
}): PlannedStageDraft[] | null {
  const live = liveScheduleInvoices(args.invoices);
  let changed = false;
  const drafts: PlannedStageDraft[] = args.stages.map((stage) => {
    const invoice = stage.invoiceId ? live.find((item) => item.id === stage.invoiceId) : null;
    const invoiceAmount = invoice ? money(invoice.grossTotal || invoice.depositAmount) : 0;
    const drifted = !!invoice && invoiceAmount > 0 && invoiceAmount !== money(stage.issuedAmount);
    if (drifted) changed = true;
    const issuedAmount = drifted ? invoiceAmount : stage.issuedAmount;
    return {
      sortOrder: stage.sortOrder,
      label: stage.label,
      kind: stage.kind,
      plannedPercent: stage.plannedPercent,
      plannedAmount: stage.status === 'issued' ? money(issuedAmount) : stage.plannedAmount,
      invoiceId: stage.invoiceId,
      issuedAmount,
      status: stage.status,
    };
  });
  if (!changed) return null;
  return redistributeFuturePlanned({
    drafts,
    invoices: args.invoices,
    grossTotal: args.grossTotal,
  });
}

export function rollbackIssuedStage(args: {
  stages: PaymentScheduleStage[];
  invoiceId: string;
  invoicesAfterDelete: ScheduleInvoice[];
  grossTotal: number;
}): { stages: PlannedStageDraft[]; needsReview: boolean } {
  const drafts: PlannedStageDraft[] = args.stages.map((stage) => {
    if (stage.invoiceId !== args.invoiceId) {
      return {
        sortOrder: stage.sortOrder,
        label: stage.label,
        kind: stage.kind,
        plannedPercent: stage.plannedPercent,
        plannedAmount: stage.status === 'issued' ? money(stage.issuedAmount) : stage.plannedAmount,
        invoiceId: stage.invoiceId,
        issuedAmount: stage.issuedAmount,
        status: stage.status,
      };
    }
    return {
      sortOrder: stage.sortOrder,
      label: stage.label,
      kind: stage.kind,
      plannedPercent: stage.plannedPercent,
      plannedAmount: money(stage.issuedAmount ?? stage.plannedAmount),
      invoiceId: null,
      issuedAmount: null,
      status: 'expected',
    };
  });
  const redistributed = redistributeFuturePlanned({
    drafts,
    invoices: args.invoicesAfterDelete,
    grossTotal: args.grossTotal,
  });
  return {
    stages: redistributed,
    needsReview: scheduleNeedsReview({
      grossTotal: args.grossTotal,
      stages: redistributed.map((draft) => ({
        status: draft.status || 'expected',
        plannedAmount: draft.plannedAmount,
        issuedAmount: draft.issuedAmount ?? null,
      })),
      invoices: args.invoicesAfterDelete,
    }),
  };
}

export function presentScheduleStages(args: {
  stages: PaymentScheduleStage[];
  grossTotal: number;
  invoices: ScheduleInvoice[];
  finalSettled: boolean;
  firstAccepted?: boolean;
}): PresentedScheduleStage[] {
  const resolved = resolveStageStatuses(args);
  const remaining = remainingToInvoice(args);
  const future = resolved.filter((stage) => stage.status !== 'issued');
  const lastFutureId = future[future.length - 1]?.id;
  let allocatedFuture = 0;
  const nextReadyId = nextIssuableStage(resolved)?.id || null;
  return resolved.map((stage) => {
    let amount = money(stage.issuedAmount ?? stage.plannedAmount);
    if (stage.status !== 'issued') {
      if (stage.id === lastFutureId) {
        amount = money(remaining - allocatedFuture);
      } else {
        amount = money(stage.plannedAmount);
        allocatedFuture = money(allocatedFuture + amount);
      }
    }
    const amountCaption = stage.status === 'issued'
      ? 'Issued'
      : stage.status === 'ready'
        ? 'Ready'
        : 'Expected';
    return {
      id: stage.id,
      sortOrder: stage.sortOrder,
      label: stage.label,
      kind: stage.kind,
      status: stage.status,
      amount: Math.max(amount, 0),
      amountCaption,
      invoiceId: stage.invoiceId,
      canIssue: stage.id === nextReadyId,
      locked: stage.status === 'issued',
    };
  });
}

export function editedScheduleIsReconciled(args: {
  issuedTotal: number;
  futurePlannedTotal: number;
  grossTotal: number;
}): boolean {
  return amountsMatch(money(args.issuedTotal) + money(args.futurePlannedTotal), args.grossTotal);
}

export function quoteHasPaymentStructure(quote?: {
  paymentStructureApplicable?: boolean | null;
  paymentStructurePattern?: string | null;
} | null): boolean {
  if (!quote?.paymentStructureApplicable) return false;
  return String(quote.paymentStructurePattern || '').trim().length > 0;
}

export function snapshotSchedule(schedule: PaymentSchedule): Record<string, unknown> {
  return {
    source: schedule.source,
    quotedPattern: schedule.quotedPattern,
    needsReview: schedule.needsReview,
    agreedValueAtLastCalc: schedule.agreedValueAtLastCalc,
    stages: schedule.stages.map((stage) => ({
      id: stage.id,
      label: stage.label,
      kind: stage.kind,
      status: stage.status,
      plannedAmount: stage.plannedAmount,
      issuedAmount: stage.issuedAmount,
      invoiceId: stage.invoiceId,
    })),
  };
}
